"use client";
import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

const STORAGE_KEY = 'podcast_player_state';

const PodcastContext = createContext();

export function PodcastProvider({ children }) {
    const [currentEpisode, setCurrentEpisode] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPlayerVisible, setIsPlayerVisible] = useState(false);
    const audioRef = useRef(null);

    // Load saved state on mount
    useEffect(() => {
        try {
            const savedState = localStorage.getItem(STORAGE_KEY);
            if (savedState) {
                const state = JSON.parse(savedState);
                // Only restore if player was visible (not closed)
                if (state.isPlayerVisible && state.currentEpisode) {
                    setCurrentEpisode(state.currentEpisode);
                    setIsPlayerVisible(true);
                    // Don't auto-play, let user decide
                    setIsPlaying(false);
                }
            }
        } catch (error) {
            console.error('Error loading podcast player state:', error);
        }
    }, []);

    // Save state whenever it changes
    useEffect(() => {
        if (currentEpisode && isPlayerVisible) {
            try {
                const stateToSave = {
                    currentEpisode: {
                        id: currentEpisode.id,
                        title: currentEpisode.title,
                        author: currentEpisode.author,
                        audioUrl: currentEpisode.audioUrl,
                        featured_image_urls: currentEpisode.featured_image_urls
                    },
                    isPlayerVisible,
                    isPlaying
                };
                localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
            } catch (error) {
                console.error('Error saving podcast player state:', error);
            }
        } else if (!isPlayerVisible) {
            // Clear saved state if player is closed
            try {
                localStorage.removeItem(STORAGE_KEY);
            } catch (error) {
                console.error('Error clearing podcast player state:', error);
            }
        }
    }, [currentEpisode, isPlayerVisible, isPlaying]);

    const playEpisode = (episode) => {
        // If playing the same episode, just toggle play
        if (currentEpisode && currentEpisode.id === episode.id) {
            setIsPlaying(true);
            return;
        }

        // New episode
        setCurrentEpisode(episode);
        setIsPlaying(true);
        setIsPlayerVisible(true);
    };

    const pauseEpisode = () => {
        setIsPlaying(false);
    };

    const togglePlay = () => {
        setIsPlaying(!isPlaying);
    };

    const closePlayer = () => {
        setIsPlaying(false);
        setIsPlayerVisible(false);
        // Clear saved state and progress when player is closed
        try {
            localStorage.removeItem(STORAGE_KEY);
            // Clear progress for current episode if it exists
            if (currentEpisode) {
                const progressKey = `podcast_progress_${currentEpisode.id}`;
                localStorage.removeItem(progressKey);
            }
        } catch (error) {
            console.error('Error clearing podcast player state:', error);
        }
        setTimeout(() => {
            setCurrentEpisode(null);
        }, 300); // Wait for animation
    };

    return (
        <PodcastContext.Provider
            value={{
                currentEpisode,
                isPlaying,
                setIsPlaying,
                isPlayerVisible,
                playEpisode,
                pauseEpisode,
                togglePlay,
                closePlayer,
                audioRef
            }}
        >
            {children}
        </PodcastContext.Provider>
    );
}

export function usePodcast() {
    const context = useContext(PodcastContext);
    if (!context) {
        throw new Error('usePodcast must be used within a PodcastProvider');
    }
    return context;
}
